import { useState, useEffect } from 'react';
import { signalRService } from '../../services/signalr/SignalRService';
import { HubMethods, EconomySnapshot } from '../../services/signalr/types';
import './SocialConsentSection.css';

interface ConsentControl {
  key: 'potions' | 'calls';
  title: string;
  desc: string;
  ariaLabel: string;
  hubMethod: string;
  options: { value: number; label: string; desc: string }[];
}

const CONTROLS: ConsentControl[] = [
  {
    key: 'potions',
    title: 'Effect Potions',
    desc: 'Who can hit you with effect potions? Effects are cosmetic, short, and always shake-off-able — this controls who may even try. Blocked users can never target you.',
    ariaLabel: 'Effect potion consent',
    hubMethod: HubMethods.SetSocialConsent,
    options: [
      { value: 0, label: 'Everyone', desc: 'Anyone in the room can splash you' },
      { value: 1, label: 'People I liked', desc: 'Only people you liked can splash you (default)' },
      { value: 2, label: 'Nobody', desc: 'No one can target you with effect potions' },
    ],
  },
  {
    key: 'calls',
    title: 'Calls',
    desc: 'Who can ring you for a private in-room call? A ring is only a request — nothing connects until you accept, and declining looks the same as not answering. Blocked users can never call you.',
    ariaLabel: 'Call consent',
    hubMethod: HubMethods.SetCallConsent,
    options: [
      { value: 0, label: 'Everyone', desc: 'Anyone in the room may ring you (default — you still approve each call)' },
      { value: 1, label: 'People I liked', desc: 'Only people you liked can ring you' },
      { value: 2, label: 'Nobody', desc: 'No one can call you' },
    ],
  },
];

/**
 * Consent settings for social play (Alchemist's Cove §6 + calls). Rendered inside
 * PreferencesPanel. Saves immediately on change (server-side PlayerSocialSettings
 * row); the potion-throw and call-ring paths enforce these server-side — real
 * safety switches, not UI filters.
 * Self-gates: renders nothing if the server doesn't expose the economy surface yet.
 */
export function SocialConsentSection() {
  const [consent, setConsent] = useState<{ potions: number; calls: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const json = await signalRService.invoke<string>(HubMethods.GetMyEconomy);
        if (cancelled || !json) return;
        const snap = JSON.parse(json) as EconomySnapshot;
        if (snap.ok) {
          setConsent({
            potions: snap.settings?.effectPotions ?? 1,
            calls: snap.settings?.calls ?? 0,
          });
        }
      } catch {
        // Older server — section stays hidden.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const choose = async (control: ConsentControl, value: number) => {
    if (saving || consent === null) return;
    const previous = consent;
    setConsent({ ...consent, [control.key]: value });
    setSaving(true);
    try {
      const json = await signalRService.invoke<string>(control.hubMethod, value);
      const result = JSON.parse(json) as { ok: boolean };
      if (!result.ok) setConsent(previous);
      else {
        setSavedTick(true);
        setTimeout(() => setSavedTick(false), 2000);
      }
    } catch {
      setConsent(previous);
    } finally {
      setSaving(false);
    }
  };

  if (consent === null) return null;

  return (
    <section className="pref-section">
      <h3>Social Play {savedTick && <span className="consent-saved">✓ saved</span>}</h3>
      {CONTROLS.map((control) => (
        <div key={control.key} className="consent-block">
          <h4 className="consent-block-title">{control.title}</h4>
          <p className="section-desc">{control.desc}</p>
          <div className="consent-options" role="radiogroup" aria-label={control.ariaLabel}>
            {control.options.map(opt => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={consent[control.key] === opt.value}
                className={`consent-option ${consent[control.key] === opt.value ? 'selected' : ''}`}
                disabled={saving}
                onClick={() => choose(control, opt.value)}
              >
                <span className="consent-option-label">{opt.label}</span>
                <span className="consent-option-desc">{opt.desc}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
